import React, { useEffect, useState } from "react";
import { useInView } from "./Reveal";

/**
 * Numero animato che conta da 0 al valore finale quando entra in viewport.
 *
 * Accetta stringhe come "80%", "500+", "€ 2M", "25 Anni" — estraiamo la
 * parte numerica, animiamo quella, e ricomponiamo prefisso/suffisso.
 *
 * Props:
 *  - value: string (es. "80%", "500+", "€ 2M")
 *  - duration: ms di animazione — default 1400
 *  - className: passata al wrapper
 */
const parseValue = (raw) => {
  const s = String(raw || "");
  // Cerca il primo numero (anche decimale). "€ 2M" -> "2", "25 Anni" -> "25"
  const match = s.match(/([\d]+(?:[.,]\d+)?)/);
  if (!match) return { prefix: s, num: null, suffix: "" };
  const num = parseFloat(match[1].replace(",", "."));
  const idx = match.index;
  const prefix = s.slice(0, idx);
  const suffix = s.slice(idx + match[1].length);
  return { prefix, num, suffix };
};

// Easing "ease-out-cubic": partenza rapida, decelerazione graduale
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

const AnimatedNumber = ({ value, duration = 1400, className = "" }) => {
  const [ref, inView] = useInView({ threshold: 0.3 });
  const parsed = parseValue(value);
  const [current, setCurrent] = useState(parsed.num ?? 0);

  useEffect(() => {
    if (!inView || parsed.num === null) return;
    let raf;
    const start = performance.now();
    const target = parsed.num;
    const from = 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const v = from + (target - from) * easeOut(t);
      setCurrent(v);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, duration, parsed.num]);

  // Se non c'e' parte numerica, ritorna la stringa cosi' com'e'
  if (parsed.num === null) {
    return (
      <span ref={ref} className={className}>
        {value}
      </span>
    );
  }

  // Formatta il numero corrente rispettando decimali del valore finale
  const hasDecimals = String(parsed.num).includes(".");
  const formatted = hasDecimals
    ? current.toFixed(1).replace(".", ",")
    : Math.round(current).toLocaleString("it-IT");

  return (
    <span ref={ref} className={className}>
      {parsed.prefix}
      {formatted}
      {parsed.suffix}
    </span>
  );
};

export default AnimatedNumber;
